import { Box, CircularProgress, keyframes } from "@mui/material";

import FullLogoPrimary from "@/assets/logos/full-logo-primary.png";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";

const logoPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.04);
    opacity: 0.92;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% {
    opacity: 0.25;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-4px);
  }
`;

type ImpersonationAccessScreenProps = {
  mode: "loading" | "error";
  message?: string;
  onBack?: () => void;
};

export default function ImpersonationAccessScreen({
  mode,
  message,
  onBack,
}: ImpersonationAccessScreenProps) {
  const isLoading = mode === "loading";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#ffffff",
        px: 3,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          maxWidth: 420,
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Box
          component="img"
          src={FullLogoPrimary}
          alt="Talk2Buy"
          sx={{
            height: { xs: 64, sm: 80 },
            objectFit: "contain",
            animation: isLoading ? `${logoPulse} 2.2s ease-in-out infinite` : "none",
          }}
        />

        {isLoading ? (
          <>
            <CircularProgress size={36} thickness={4} sx={{ color: "#16D3D9" }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <MDTypography variant="h5" color="dark" fontWeight="medium">
                Iniciando acesso
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Preparando sua sessão de suporte
              </MDTypography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#16D3D9",
                    animation: `${dotPulse} 1.2s ease-in-out infinite`,
                    animationDelay: `${index * 0.15}s`,
                  }}
                />
              ))}
            </Box>
          </>
        ) : (
          <>
            <MDTypography variant="h5" color="dark" fontWeight="medium">
              Não foi possível entrar
            </MDTypography>

            <MDTypography variant="body2" color="text" sx={{ lineHeight: 1.6 }}>
              {message || "Falha ao realizar login"}
            </MDTypography>

            {onBack && (
              <MDButton variant="gradient" color="primary" onClick={onBack} sx={{ mt: 1, minWidth: 220 }}>
                Voltar para Login
              </MDButton>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
